import React, { useState, useRef } from 'react';
import { useParams, Link } from 'react-router-dom';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer, BarChart, Bar, PieChart, Pie, Cell, ScatterChart, Scatter, ZAxis, AreaChart, Area } from 'recharts';
import { mockPatients } from '../utils/mockData';
import { format } from 'date-fns';
import jsPDF from 'jspdf';
import { saveAs } from 'file-saver';

interface TreatmentSession {
  date: string;
  vibrationUsage: number;
  redLightTherapy: number;
  surfaceEMG: number;
}

interface Goal {
  id: string;
  description: string;
  target: number;
  current: number;
}

interface BodyAreaNote {
  area: string;
  note: string;
}

interface PainAndComplianceData {
  date: string;
  pain: number;
  compliance: number;
}

interface TreatmentPlan {
  id: string;
  name: string;
  therapies: {
    type: 'vibration' | 'redLight' | 'EMG';
    intensity: number;
    duration: number;
  }[];
  frequency: string;
  instructions: string;
}

interface PainSymptomEntry {
  date: string;
  painLevel: number;
  symptoms: string[];
  notes: string;
}

interface AdherenceData {
  date: string;
  exercisesCompleted: number;
  exercisesTotal: number;
  myoworksUsage: number;
  remindersSent: number;
  remindersResponded: number;
}

interface TreatmentEfficacy {
  treatment: string;
  painReduction: number;
  frequency: number;
}

interface TreatmentMilestone {
  date: string;
  description: string;
  expectedProgress: number;
  actualProgress: number;
}

interface SelectionArea {
  startX: number;
  startY: number;
  endX: number;
  endY: number;
}

const bodyAreas = [
  'Head', 'Neck', 'Shoulders', 'Arms', 'Elbows', 'Wrists', 'Hands',
  'Upper Back', 'Lower Back', 'Hips', 'Legs', 'Knees', 'Ankles', 'Feet'
];

const PatientDetail = () => {
  const { id } = useParams<{ id: string }>();
  const patient = mockPatients.find(p => p.id === id);
  const [focusArea, setFocusArea] = useState<{ x: number, y: number } | null>(null);
  const [chiropractorNotes, setChiropractorNotes] = useState<string[]>([]);
  const [newNote, setNewNote] = useState('');
  const [goals, setGoals] = useState<Goal[]>([
    { id: '1', description: 'Reduce pain level', target: 2, current: 4 },
    { id: '2', description: 'Increase mobility', target: 8, current: 6 },
  ]);
  const [newGoal, setNewGoal] = useState({ description: '', target: 0 });
  const [bodyAreaNotes, setBodyAreaNotes] = useState<BodyAreaNote[]>([]);
  const [selectedBodyArea, setSelectedBodyArea] = useState<string | null>(null);
  const [newBodyAreaNote, setNewBodyAreaNote] = useState('');
  const [showCompliance, setShowCompliance] = useState(false);
  const [treatmentPlans, setTreatmentPlans] = useState<TreatmentPlan[]>([]);
  const [newPlan, setNewPlan] = useState<TreatmentPlan>({
    id: '',
    name: '',
    therapies: [],
    frequency: '',
    instructions: '',
  });
  const [painSymptomEntries, setPainSymptomEntries] = useState<PainSymptomEntry[]>([]);
  const [newEntry, setNewEntry] = useState<PainSymptomEntry>({
    date: format(new Date(), 'yyyy-MM-dd'),
    painLevel: 0,
    symptoms: [],
    notes: '',
  });
  const [adherenceData, setAdherenceData] = useState<AdherenceData[]>([
    { date: '2023-05-01', exercisesCompleted: 8, exercisesTotal: 10, myoworksUsage: 25, remindersSent: 3, remindersResponded: 2 },
    { date: '2023-05-02', exercisesCompleted: 7, exercisesTotal: 10, myoworksUsage: 20, remindersSent: 2, remindersResponded: 2 },
    { date: '2023-05-03', exercisesCompleted: 9, exercisesTotal: 10, myoworksUsage: 30, remindersSent: 2, remindersResponded: 1 },
    { date: '2023-05-04', exercisesCompleted: 10, exercisesTotal: 10, myoworksUsage: 35, remindersSent: 1, remindersResponded: 1 },
    { date: '2023-05-05', exercisesCompleted: 8, exercisesTotal: 10, myoworksUsage: 28, remindersSent: 2, remindersResponded: 2 },
  ]);
  const [treatmentEfficacy, setTreatmentEfficacy] = useState<TreatmentEfficacy[]>([
    { treatment: 'Vibration Therapy', painReduction: 2.5, frequency: 15 },
    { treatment: 'Red Light Therapy', painReduction: 1.8, frequency: 10 },
    { treatment: 'EMG Biofeedback', painReduction: 3.2, frequency: 20 },
    { treatment: 'Stretching Exercises', painReduction: 1.5, frequency: 25 },
    { treatment: 'Strength Training', painReduction: 2.0, frequency: 18 },
  ]);
  const [treatmentMilestones, setTreatmentMilestones] = useState<TreatmentMilestone[]>([
    { date: '2023-05-01', description: 'Initial Assessment', expectedProgress: 0, actualProgress: 0 },
    { date: '2023-05-15', description: 'First Follow-up', expectedProgress: 20, actualProgress: 15 },
    { date: '2023-06-01', description: 'Midpoint Evaluation', expectedProgress: 50, actualProgress: 45 },
    { date: '2023-06-15', description: 'Adjustment of Plan', expectedProgress: 70, actualProgress: 60 },
    { date: '2023-07-01', description: 'Final Assessment', expectedProgress: 100, actualProgress: 85 },
  ]);
  const [selectionArea, setSelectionArea] = useState<SelectionArea | null>(null);
  const [isSelecting, setIsSelecting] = useState(false);
  const imageRef = useRef<HTMLImageElement>(null);
  const [imageScale, setImageScale] = useState({ x: 1, y: 1 });

  if (!patient) {
    return <div className="flex items-center justify-center h-full">Patient not found</div>;
  }

  const painData = [
    { date: '2023-05-01', pain: 7 },
    { date: '2023-05-02', pain: 6 },
    { date: '2023-05-03', pain: 5 },
    { date: '2023-05-04', pain: 6 },
    { date: '2023-05-05', pain: 4 },
  ];

  const painAndComplianceData: PainAndComplianceData[] = [
    { date: '2023-05-01', pain: 7, compliance: 80 },
    { date: '2023-05-02', pain: 6, compliance: 85 },
    { date: '2023-05-03', pain: 5, compliance: 90 },
    { date: '2023-05-04', pain: 6, compliance: 75 },
    { date: '2023-05-05', pain: 4, compliance: 95 },
  ];

  const treatmentHistory: TreatmentSession[] = [
    { date: '2023-05-01', vibrationUsage: 10, redLightTherapy: 5, surfaceEMG: 20 },
    { date: '2023-05-02', vibrationUsage: 12, redLightTherapy: 6, surfaceEMG: 18 },
    { date: '2023-05-03', vibrationUsage: 15, redLightTherapy: 7, surfaceEMG: 22 },
    { date: '2023-05-04', vibrationUsage: 11, redLightTherapy: 5, surfaceEMG: 19 },
    { date: '2023-05-05', vibrationUsage: 13, redLightTherapy: 6, surfaceEMG: 21 },
  ];

  const updateImageScale = () => {
    if (imageRef.current) {
      const { naturalWidth, naturalHeight, width, height } = imageRef.current;
      setImageScale({
        x: naturalWidth / width,
        y: naturalHeight / height
      });
    }
  };

  const handleImageLoad = () => {
    updateImageScale();
  };

  const handleMouseDown = (event: React.MouseEvent<HTMLImageElement>) => {
    if (imageRef.current) {
      const rect = imageRef.current.getBoundingClientRect();
      const x = (event.clientX - rect.left) * imageScale.x;
      const y = (event.clientY - rect.top) * imageScale.y;
      setSelectionArea({ startX: x, startY: y, endX: x, endY: y });
      setIsSelecting(true);
    }
  };

  const handleMouseMove = (event: React.MouseEvent<HTMLImageElement>) => {
    if (isSelecting && imageRef.current) {
      const rect = imageRef.current.getBoundingClientRect();
      const x = (event.clientX - rect.left) * imageScale.x;
      const y = (event.clientY - rect.top) * imageScale.y;
      setSelectionArea(prev => prev ? { ...prev, endX: x, endY: y } : null);
    }
  };

  const handleMouseUp = () => {
    setIsSelecting(false);
  };

  const handleMouseLeave = () => {
    setIsSelecting(false);
  };

  const addBodyAreaNote = () => {
    if (selectedBodyArea && newBodyAreaNote.trim()) {
      setBodyAreaNotes([...bodyAreaNotes, { area: selectedBodyArea, note: newBodyAreaNote.trim() }]);
      setNewBodyAreaNote('');
      setSelectedBodyArea(null);
    }
  };

  const addNote = () => {
    if (newNote.trim()) {
      setChiropractorNotes([...chiropractorNotes, newNote.trim()]);
      setNewNote('');
    }
  };

  const addGoal = () => {
    if (newGoal.description && newGoal.target > 0) {
      setGoals([...goals, { ...newGoal, id: Date.now().toString(), current: 0 }]);
      setNewGoal({ description: '', target: 0 });
    }
  };

  const updateGoalProgress = (id: string, newCurrent: number) => {
    setGoals(goals.map(goal => 
      goal.id === id ? { ...goal, current: newCurrent } : goal
    ));
  };

  const addTreatmentPlan = () => {
    if (newPlan.name && newPlan.therapies.length > 0) {
      setTreatmentPlans([...treatmentPlans, { ...newPlan, id: Date.now().toString() }]);
      setNewPlan({
        id: '',
        name: '',
        therapies: [],
        frequency: '',
        instructions: '',
      });
    }
  };

  const addTherapyToNewPlan = (type: 'vibration' | 'redLight' | 'EMG') => {
    setNewPlan({
      ...newPlan,
      therapies: [...newPlan.therapies, { type, intensity: 1, duration: 5 }],
    });
  };

  const updateTherapyInNewPlan = (index: number, field: 'intensity' | 'duration', value: number) => {
    const updatedTherapies = newPlan.therapies.map((therapy, i) =>
      i === index ? { ...therapy, [field]: value } : therapy
    );
    setNewPlan({ ...newPlan, therapies: updatedTherapies });
  };

  const removeTreatmentPlan = (id: string) => {
    setTreatmentPlans(treatmentPlans.filter(plan => plan.id !== id));
  };

  const generatePrintableInstructions = (plan: TreatmentPlan) => {
    const pdf = new jsPDF();
    
    // Helper function to add a new page if needed
    const addPageIfNeeded = (y: number, threshold: number = 280) => {
      if (y > threshold) {
        pdf.addPage();
        return 20;
      }
      return y;
    };

    // Title
    pdf.setFontSize(20);
    pdf.text(`Treatment Plan: ${plan.name}`, 20, 20);

    // Plan details
    pdf.setFontSize(12);
    let y = 40;
    pdf.text(`Frequency: ${plan.frequency}`, 20, y);

    // Therapies
    y = addPageIfNeeded(y += 20);
    pdf.setFontSize(16);
    pdf.text('Therapies', 20, y);
    pdf.setFontSize(12);
    plan.therapies.forEach((therapy, index) => {
      y = addPageIfNeeded(y += 15);
      pdf.text(`${index + 1}. ${therapy.type}`, 30, y);
      pdf.text(`   Intensity: ${therapy.intensity}`, 30, y += 10);
      pdf.text(`   Duration: ${therapy.duration} minutes`, 30, y += 10);
    });

    // Instructions
    y = addPageIfNeeded(y += 20);
    pdf.setFontSize(16);
    pdf.text('Instructions', 20, y);
    pdf.setFontSize(12);
    
    // Split instructions into lines
    const splitInstructions = pdf.splitTextToSize(plan.instructions, 170);
    splitInstructions.forEach((line: string) => {
      y = addPageIfNeeded(y += 10);
      pdf.text(line, 30, y);
    });

    // Save the PDF
    const pdfBlob = pdf.output('blob');
    saveAs(pdfBlob, `${plan.name}_instructions.pdf`);
  };

  const sendEmailInstructions = (plan: TreatmentPlan) => {
    // This function would send an email with the instructions
    // For now, we'll just alert that an email would be sent
    alert(`Email sent with instructions for ${plan.name}`);
  };

  const addPainSymptomEntry = () => {
    if (newEntry.painLevel >= 0 && newEntry.painLevel <= 10) {
      setPainSymptomEntries([...painSymptomEntries, newEntry]);
      setNewEntry({
        date: format(new Date(), 'yyyy-MM-dd'),
        painLevel: 0,
        symptoms: [],
        notes: '',
      });
    }
  };

  const toggleSymptom = (symptom: string) => {
    setNewEntry(prev => ({
      ...prev,
      symptoms: prev.symptoms.includes(symptom)
        ? prev.symptoms.filter(s => s !== symptom)
        : [...prev.symptoms, symptom],
    }));
  };

  const calculateAverageAdherence = () => {
    const totalExercises = adherenceData.reduce((sum, day) => sum + day.exercisesTotal, 0);
    const completedExercises = adherenceData.reduce((sum, day) => sum + day.exercisesCompleted, 0);
    return (completedExercises / totalExercises) * 100;
  };

  const averageAdherence = calculateAverageAdherence();

  const pieChartData = [
    { name: 'Completed', value: averageAdherence },
    { name: 'Missed', value: 100 - averageAdherence },
  ];

  const COLORS = ['#0088FE', '#FF8042'];

  const generateAIRecommendations = () => {
    // This is a placeholder for AI-driven recommendations
    // In a real application, this would involve more complex analysis
    const mostEffective = treatmentEfficacy.reduce((prev, current) => 
      (current.painReduction > prev.painReduction) ? current : prev
    );

    const leastEffective = treatmentEfficacy.reduce((prev, current) => 
      (current.painReduction < prev.painReduction) ? current : prev
    );

    return [
      `Increase frequency of ${mostEffective.treatment} as it shows the highest pain reduction.`,
      `Consider reducing or modifying ${leastEffective.treatment} as it shows the lowest pain reduction.`,
      'Evaluate the combination of treatments for potential synergistic effects.',
      'Monitor patient adherence to the most effective treatments and provide additional support if needed.',
    ];
  };

  const aiRecommendations = generateAIRecommendations();

  const updateMilestoneProgress = (index: number, actualProgress: number) => {
    const updatedMilestones = [...treatmentMilestones];
    updatedMilestones[index].actualProgress = actualProgress;
    setTreatmentMilestones(updatedMilestones);
  };

  const generatePDFSummary = () => {
    const pdf = new jsPDF();
    const patient = mockPatients.find(p => p.id === id);

    if (!patient) {
      alert('Patient not found');
      return;
    }

    // Helper function to add a new page if needed
    const addPageIfNeeded = (y: number, threshold: number = 280) => {
      if (y > threshold) {
        pdf.addPage();
        return 20;
      }
      return y;
    };

    // Title and basic info
    pdf.setFontSize(20);
    pdf.text('Patient Summary', 20, 20);

    pdf.setFontSize(12);
    let y = 40;
    pdf.text(`Name: ${patient.name}`, 20, y);
    pdf.text(`Age: ${patient.age}`, 20, y += 10);
    pdf.text(`Gender: ${patient.gender}`, 20, y += 10);
    pdf.text(`Injury: ${patient.injury}`, 20, y += 10);

    // Treatment Goals
    y = addPageIfNeeded(y += 20);
    pdf.setFontSize(16);
    pdf.text('Treatment Goals', 20, y);
    pdf.setFontSize(12);
    goals.forEach((goal, index) => {
      y = addPageIfNeeded(y += 10);
      pdf.text(`- ${goal.description}: ${goal.current}/${goal.target}`, 30, y);
    });

    // Pain Data
    y = addPageIfNeeded(y += 20);
    pdf.setFontSize(16);
    pdf.text('Recent Pain Levels', 20, y);
    pdf.setFontSize(12);
    painData.forEach((entry, index) => {
      y = addPageIfNeeded(y += 10);
      pdf.text(`- ${entry.date}: ${entry.pain}`, 30, y);
    });

    // Treatment Plans
    y = addPageIfNeeded(y += 20);
    pdf.setFontSize(16);
    pdf.text('Treatment Plans', 20, y);
    pdf.setFontSize(12);
    treatmentPlans.forEach((plan, index) => {
      y = addPageIfNeeded(y += 15);
      pdf.text(`Plan: ${plan.name}`, 30, y);
      pdf.text(`Frequency: ${plan.frequency}`, 30, y += 10);
      plan.therapies.forEach((therapy, tIndex) => {
        y = addPageIfNeeded(y += 10);
        pdf.text(`- ${therapy.type}: Intensity ${therapy.intensity}, Duration ${therapy.duration} minutes`, 40, y);
      });
    });

    // Compliance Data
    y = addPageIfNeeded(y += 20);
    pdf.setFontSize(16);
    pdf.text('Treatment Plan Adherence', 20, y);
    pdf.setFontSize(12);
    y += 10;
    pdf.text(`Average Adherence: ${averageAdherence.toFixed(1)}%`, 30, y);
    
    y = addPageIfNeeded(y += 15);
    pdf.text('Myoworks Device Usage:', 30, y);
    adherenceData.forEach((day, index) => {
      y = addPageIfNeeded(y += 10);
      pdf.text(`- ${day.date}: ${day.myoworksUsage} minutes`, 40, y);
    });

    y = addPageIfNeeded(y += 15);
    pdf.text('Reminders and Responsiveness:', 30, y);
    adherenceData.forEach((day, index) => {
      y = addPageIfNeeded(y += 10);
      const responseRate = ((day.remindersResponded / day.remindersSent) * 100).toFixed(1);
      pdf.text(`- ${day.date}: ${day.remindersResponded}/${day.remindersSent} (${responseRate}%)`, 40, y);
    });

    // Treatment Efficacy
    y = addPageIfNeeded(y += 20);
    pdf.setFontSize(16);
    pdf.text('Treatment Efficacy', 20, y);
    pdf.setFontSize(12);
    treatmentEfficacy.forEach((treatment, index) => {
      y = addPageIfNeeded(y += 10);
      pdf.text(`- ${treatment.treatment}: Pain Reduction ${treatment.painReduction.toFixed(1)}, Frequency ${treatment.frequency}`, 30, y);
    });

    // AI Recommendations
    y = addPageIfNeeded(y += 20);
    pdf.setFontSize(16);
    pdf.text('AI-Driven Recommendations', 20, y);
    pdf.setFontSize(12);
    aiRecommendations.forEach((recommendation, index) => {
      y = addPageIfNeeded(y += 10);
      pdf.text(`- ${recommendation}`, 30, y);
    });

    // Save the PDF
    const pdfBlob = pdf.output('blob');
    saveAs(pdfBlob, `${patient.name}_summary.pdf`);
  };

  return (
    <div className="flex flex-col h-full bg-gray-100 p-6 space-y-6">
      <div className="mb-6 flex justify-between items-center">
        <Link to="/" className="text-blue-700 hover:text-blue-900 font-medium flex items-center">
          <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 19l-7-7m0 0l7-7m-7 7h18" />
          </svg>
          Back to Patient List
        </Link>
        <button
          onClick={generatePDFSummary}
          className="bg-green-500 text-white px-4 py-2 rounded hover:bg-green-600 transition-colors"
        >
          Download PDF Summary
        </button>
      </div>
      
      <div className="bg-white rounded-lg shadow-lg p-6 border border-gray-200">
        <h2 className="text-3xl font-bold mb-4 text-gray-800 border-b pb-2">{patient.name}'s Details</h2>
        <div className="grid grid-cols-3 gap-4 text-sm text-gray-600">
          <div className="flex items-center">
            <svg className="w-5 h-5 mr-2 text-gray-500" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
            </svg>
            Age: {patient.age}
          </div>
          <div className="flex items-center">
            <svg className="w-5 h-5 mr-2 text-gray-500" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
            </svg>
            Gender: {patient.gender}
          </div>
          <div className="flex items-center">
            <svg className="w-5 h-5 mr-2 text-gray-500" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
            </svg>
            Location: {patient.location}
          </div>
        </div>
      </div>

      <div className="bg-white rounded-lg shadow-lg p-6 border border-gray-200">
        <h3 className="text-xl font-semibold mb-4 text-gray-800 border-b pb-2">Myoworks Device Information</h3>
        <div className="flex flex-col md:flex-row">
          <div className="w-full md:w-1/3 mb-4 md:mb-0">
            <img src="/myo-blade.png" alt="Myoworks Device" className="w-full rounded-lg shadow-md" />
          </div>
          <div className="w-full md:w-2/3 md:pl-6">
            <table className="min-w-full bg-white">
              <tbody>
                <tr className="border-b">
                  <td className="px-4 py-2 font-semibold">Firmware Version</td>
                  <td className="px-4 py-2">2.1.3</td>
                </tr>
                <tr className="border-b">
                  <td className="px-4 py-2 font-semibold">Date Assigned</td>
                  <td className="px-4 py-2">2023-04-15</td>
                </tr>
                <tr className="border-b">
                  <td className="px-4 py-2 font-semibold">Modules Enabled</td>
                  <td className="px-4 py-2">EMG, Red Light</td>
                </tr>
                <tr className="border-b">
                  <td className="px-4 py-2 font-semibold">Device Serial Number</td>
                  <td className="px-4 py-2">MYO-2023-78945</td>
                </tr>
                <tr className="border-b">
                  <td className="px-4 py-2 font-semibold">Remaining Battery Level</td>
                  <td className="px-4 py-2">
                    <div className="flex items-center">
                      <div className="w-full bg-gray-200 rounded-full h-2.5 mr-2">
                        <div className="bg-green-600 h-2.5 rounded-full" style={{ width: '85%' }}></div>
                      </div>
                      85%
                    </div>
                  </td>
                </tr>
                <tr className="border-b">
                  <td className="px-4 py-2 font-semibold">Last Online</td>
                  <td className="px-4 py-2">2023-05-10 14:30:22</td>
                </tr>
                <tr>
                  <td className="px-4 py-2 font-semibold">WiFi MAC Address</td>
                  <td className="px-4 py-2">00:1B:44:11:3A:B7</td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>
      </div>

      <div className="bg-white rounded-lg shadow-lg p-6 border border-gray-200">
        <h3 className="text-xl font-semibold mb-4 text-gray-800 border-b pb-2">Body Areas</h3>
        <div className="flex flex-col md:flex-row">
          <ul className="w-full md:w-1/3 pr-4 mb-4 md:mb-0 md:border-r">
            {bodyAreas.map(area => (
              <li 
                key={area} 
                className={`cursor-pointer p-2 hover:bg-gray-100 ${selectedBodyArea === area ? 'bg-blue-100' : ''}`}
                onClick={() => setSelectedBodyArea(area)}
              >
                {area}
              </li>
            ))}
          </ul>
          <div className="w-full md:w-2/3 md:pl-4">
            <h4 className="font-semibold mb-2">Body Diagram</h4>
            <img
              src="/body-outline.png"
              alt="Body Outline"
              className="w-full max-w-md mx-auto"
            />
          </div>
        </div>
        {selectedBodyArea && (
          <div className="mt-4">
            <h4 className="font-semibold mb-2">Add note for {selectedBodyArea}</h4>
            <textarea
              className="w-full p-2 border rounded mb-2"
              rows={3}
              value={newBodyAreaNote}
              onChange={(e) => setNewBodyAreaNote(e.target.value)}
              placeholder={`Add a note for ${selectedBodyArea}...`}
            ></textarea>
            <button
              className="bg-blue-500 text-white px-4 py-2 rounded hover:bg-blue-600 transition-colors"
              onClick={addBodyAreaNote}
            >
              Add Note
            </button>
          </div>
        )}
        <div className="mt-4">
          <h4 className="font-semibold mb-2">Body Area Notes</h4>
          {bodyAreaNotes.map((note, index) => (
            <div key={index} className="mb-2 p-2 bg-gray-100 rounded">
              <span className="font-semibold">{note.area}:</span> {note.note}
            </div>
          ))}
        </div>
      </div>

      <div className="bg-white rounded-lg shadow-lg p-6 border border-gray-200">
        <h3 className="text-xl font-semibold mb-4 text-gray-800 border-b pb-2">Pain Over Time</h3>
        <div className="mb-4">
          <label className="inline-flex items-center">
            <input
              type="checkbox"
              className="form-checkbox"
              checked={showCompliance}
              onChange={() => setShowCompliance(!showCompliance)}
            />
            <span className="ml-2">Show Program Compliance</span>
          </label>
        </div>
        <div className="h-64">
          <ResponsiveContainer width="100%" height="100%">
            <LineChart data={painAndComplianceData}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis dataKey="date" />
              <YAxis yAxisId="left" />
              <YAxis yAxisId="right" orientation="right" />
              <Tooltip />
              <Legend />
              <Line yAxisId="left" type="monotone" dataKey="pain" stroke="#8884d8" name="Pain Level" />
              {showCompliance && (
                <Line yAxisId="right" type="monotone" dataKey="compliance" stroke="#82ca9d" name="Program Compliance %" />
              )}
            </LineChart>
          </ResponsiveContainer>
        </div>
      </div>

      <div className="bg-white rounded-lg shadow-lg p-6 border border-gray-200">
        <h3 className="text-xl font-semibold mb-4 text-gray-800 border-b pb-2">Treatment History</h3>
        <div className="h-64">
          <ResponsiveContainer width="100%" height="100%">
            <BarChart data={treatmentHistory}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis dataKey="date" />
              <YAxis />
              <Tooltip />
              <Legend />
              <Bar dataKey="vibrationUsage" fill="#8884d8" />
              <Bar dataKey="redLightTherapy" fill="#82ca9d" />
              <Bar dataKey="surfaceEMG" fill="#ffc658" />
            </BarChart>
          </ResponsiveContainer>
        </div>
      </div>

      <div className="bg-white rounded-lg shadow-lg p-6 border border-gray-200">
        <h3 className="text-xl font-semibold mb-4 text-gray-800 border-b pb-2">Treatment Goals</h3>
        <div className="space-y-4">
          {goals.map(goal => (
            <div key={goal.id} className="flex items-center justify-between bg-gray-50 p-3 rounded-lg">
              <span className="font-medium text-gray-700">{goal.description}</span>
              <div className="flex items-center">
                <input
                  type="number"
                  value={goal.current}
                  onChange={(e) => updateGoalProgress(goal.id, Number(e.target.value))}
                  className="w-16 p-1 border rounded mr-2 text-center"
                />
                <span className="text-gray-600">/ {goal.target}</span>
              </div>
            </div>
          ))}
        </div>
        <div className="mt-4 flex flex-col sm:flex-row">
          <input
            type="text"
            value={newGoal.description}
            onChange={(e) => setNewGoal({ ...newGoal, description: e.target.value })}
            placeholder="New goal description"
            className="flex-grow p-2 border rounded mb-2 sm:mb-0 sm:mr-2"
          />
          <input
            type="number"
            value={newGoal.target}
            onChange={(e) => setNewGoal({ ...newGoal, target: Number(e.target.value) })}
            placeholder="Target"
            className="w-full sm:w-24 p-2 border rounded mb-2 sm:mb-0 sm:mr-2"
          />
          <button
            onClick={addGoal}
            className="bg-blue-500 text-white px-4 py-2 rounded hover:bg-blue-600 transition-colors"
          >
            Add Goal
          </button>
        </div>
      </div>

      <div className="bg-white rounded-lg shadow-lg p-6 border border-gray-200">
        <h3 className="text-xl font-semibold mb-4 text-gray-800 border-b pb-2">Chiropractor Notes</h3>
        <div className="h-40 overflow-y-auto border rounded p-2 mb-4">
          {chiropractorNotes.map((note, index) => (
            <div key={index} className="mb-2 p-2 bg-gray-50 rounded border border-gray-200">
              <p className="text-sm text-gray-600">{note}</p>
            </div>
          ))}
        </div>
        <textarea
          className="w-full p-2 border rounded mb-2"
          rows={3}
          value={newNote}
          onChange={(e) => setNewNote(e.target.value)}
          placeholder="Add a new note..."
        ></textarea>
        <button
          className="w-full bg-blue-500 text-white px-4 py-2 rounded hover:bg-blue-600 transition-colors"
          onClick={addNote}
        >
          Add Note
        </button>
      </div>

      <div className="bg-white rounded-lg shadow-lg p-6 border border-gray-200">
        <h3 className="text-xl font-semibold mb-4 text-gray-800 border-b pb-2">Treatment Regimen Management</h3>
        
        <div className="mb-6">
          <h4 className="text-lg font-semibold mb-2 text-gray-700">Current Treatment Plans</h4>
          {treatmentPlans.map(plan => (
            <div key={plan.id} className="bg-gray-50 p-4 rounded-lg mb-2 border border-gray-200">
              <h5 className="font-semibold text-gray-800">{plan.name}</h5>
              <p className="text-sm text-gray-600">Frequency: {plan.frequency}</p>
              <ul className="list-disc list-inside text-sm text-gray-600 mt-2">
                {plan.therapies.map((therapy, index) => (
                  <li key={index}>
                    {therapy.type}: Intensity {therapy.intensity}, Duration {therapy.duration} minutes
                  </li>
                ))}
              </ul>
              <div className="mt-3 flex space-x-2">
                <button
                  onClick={() => generatePrintableInstructions(plan)}
                  className="bg-blue-600 text-white px-3 py-1 rounded text-sm hover:bg-blue-700 transition-colors"
                >
                  Print Instructions
                </button>
                <button
                  onClick={() => sendEmailInstructions(plan)}
                  className="bg-green-600 text-white px-3 py-1 rounded text-sm hover:bg-green-700 transition-colors"
                >
                  Email Instructions
                </button>
                <button
                  onClick={() => removeTreatmentPlan(plan.id)}
                  className="bg-red-600 text-white px-3 py-1 rounded text-sm hover:bg-red-700 transition-colors"
                >
                  Remove Plan
                </button>
              </div>
            </div>
          ))}
        </div>
        
        <div>
          <h4 className="text-lg font-semibold mb-2">Create New Treatment Plan</h4>
          <input
            type="text"
            value={newPlan.name}
            onChange={(e) => setNewPlan({ ...newPlan, name: e.target.value })}
            placeholder="Plan Name"
            className="w-full p-2 border rounded mb-2"
          />
          <input
            type="text"
            value={newPlan.frequency}
            onChange={(e) => setNewPlan({ ...newPlan, frequency: e.target.value })}
            placeholder="Frequency (e.g., 3 times per week)"
            className="w-full p-2 border rounded mb-2"
          />
          <div className="mb-2">
            <button
              onClick={() => addTherapyToNewPlan('vibration')}
              className="bg-blue-500 text-white px-2 py-1 rounded mr-2"
            >
              Add Vibration Therapy
            </button>
            <button
              onClick={() => addTherapyToNewPlan('redLight')}
              className="bg-red-500 text-white px-2 py-1 rounded mr-2"
            >
              Add Red Light Therapy
            </button>
            <button
              onClick={() => addTherapyToNewPlan('EMG')}
              className="bg-green-500 text-white px-2 py-1 rounded"
            >
              Add EMG Therapy
            </button>
          </div>
          {newPlan.therapies.map((therapy, index) => (
            <div key={index} className="flex items-center mb-2">
              <span className="mr-2">{therapy.type}:</span>
              <input
                type="number"
                value={therapy.intensity}
                onChange={(e) => updateTherapyInNewPlan(index, 'intensity', Number(e.target.value))}
                placeholder="Intensity"
                className="w-20 p-1 border rounded mr-2"
              />
              <input
                type="number"
                value={therapy.duration}
                onChange={(e) => updateTherapyInNewPlan(index, 'duration', Number(e.target.value))}
                placeholder="Duration (minutes)"
                className="w-32 p-1 border rounded"
              />
            </div>
          ))}
          <textarea
            value={newPlan.instructions}
            onChange={(e) => setNewPlan({ ...newPlan, instructions: e.target.value })}
            placeholder="Instructions for the patient"
            className="w-full p-2 border rounded mb-2"
            rows={4}
          ></textarea>
          <button
            onClick={addTreatmentPlan}
            className="bg-blue-500 text-white px-4 py-2 rounded hover:bg-blue-600 transition-colors"
          >
            Add Treatment Plan
          </button>
        </div>
      </div>

      <div className="bg-white rounded-lg shadow-lg p-6 border border-gray-200">
        <h3 className="text-xl font-semibold mb-4 text-gray-800 border-b pb-2">Pain and Symptom Tracker</h3>
        <div className="mb-4">
          <h4 className="font-semibold mb-2">Add New Entry</h4>
          <div className="flex flex-wrap gap-4">
            <input
              type="date"
              value={newEntry.date}
              onChange={(e) => setNewEntry({ ...newEntry, date: e.target.value })}
              className="p-2 border rounded"
            />
            <input
              type="number"
              min="0"
              max="10"
              value={newEntry.painLevel}
              onChange={(e) => setNewEntry({ ...newEntry, painLevel: parseInt(e.target.value) })}
              placeholder="Pain Level (0-10)"
              className="p-2 border rounded"
            />
          </div>
          <div className="mt-2">
            <p className="font-semibold mb-1">Symptoms:</p>
            <div className="flex flex-wrap gap-2">
              {['Headache', 'Nausea', 'Dizziness', 'Fatigue', 'Stiffness'].map(symptom => (
                <button
                  key={symptom}
                  onClick={() => toggleSymptom(symptom)}
                  className={`px-2 py-1 rounded text-sm ${
                    newEntry.symptoms.includes(symptom)
                      ? 'bg-blue-500 text-white'
                      : 'bg-gray-200 text-gray-700'
                  }`}
                >
                  {symptom}
                </button>
              ))}
            </div>
          </div>
          <textarea
            value={newEntry.notes}
            onChange={(e) => setNewEntry({ ...newEntry, notes: e.target.value })}
            placeholder="Additional notes..."
            className="w-full p-2 border rounded mt-2"
            rows={3}
          ></textarea>
          <button
            onClick={addPainSymptomEntry}
            className="mt-2 bg-blue-500 text-white px-4 py-2 rounded hover:bg-blue-600 transition-colors"
          >
            Add Entry
          </button>
        </div>
        <div>
          <h4 className="font-semibold mb-2">Pain and Symptom History</h4>
          <div className="overflow-x-auto">
            <table className="min-w-full bg-white">
              <thead className="bg-gray-100">
                <tr>
                  <th className="px-4 py-2 text-left">Date</th>
                  <th className="px-4 py-2 text-left">Pain Level</th>
                  <th className="px-4 py-2 text-left">Symptoms</th>
                  <th className="px-4 py-2 text-left">Notes</th>
                </tr>
              </thead>
              <tbody>
                {painSymptomEntries.map((entry, index) => (
                  <tr key={index} className="border-t">
                    <td className="px-4 py-2">{entry.date}</td>
                    <td className="px-4 py-2">{entry.painLevel}</td>
                    <td className="px-4 py-2">{entry.symptoms.join(', ')}</td>
                    <td className="px-4 py-2">{entry.notes}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </div>

      <div className="bg-white rounded-lg shadow-lg p-6 border border-gray-200">
        <h3 className="text-xl font-semibold mb-4 text-gray-800 border-b pb-2">Treatment Plan Adherence</h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div>
            <h4 className="font-semibold mb-2">Exercise Completion</h4>
            <div className="flex items-center">
              <div className="w-32 h-32">
                <ResponsiveContainer width="100%" height="100%">
                  <PieChart>
                    <Pie
                      data={pieChartData}
                      cx="50%"
                      cy="50%"
                      innerRadius={40}
                      outerRadius={60}
                      fill="#8884d8"
                      dataKey="value"
                    >
                      {pieChartData.map((entry, index) => (
                        <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                      ))}
                    </Pie>
                  </PieChart>
                </ResponsiveContainer>
              </div>
              <div className="ml-4">
                <p className="text-2xl font-bold">{averageAdherence.toFixed(1)}%</p>
                <p className="text-gray-600">Average Adherence</p>
              </div>
            </div>
          </div>
          <div>
            <h4 className="font-semibold mb-2">Myoworks Device Usage</h4>
            <div className="h-32">
              <ResponsiveContainer width="100%" height="100%">
                <LineChart data={adherenceData}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="date" />
                  <YAxis />
                  <Tooltip />
                  <Line type="monotone" dataKey="myoworksUsage" stroke="#8884d8" name="Minutes Used" />
                </LineChart>
              </ResponsiveContainer>
            </div>
          </div>
        </div>
        <div className="mt-6">
          <h4 className="font-semibold mb-2">Reminders and Responsiveness</h4>
          <div className="overflow-x-auto">
            <table className="min-w-full bg-white">
              <thead className="bg-gray-100">
                <tr>
                  <th className="px-4 py-2 text-left">Date</th>
                  <th className="px-4 py-2 text-left">Reminders Sent</th>
                  <th className="px-4 py-2 text-left">Reminders Responded</th>
                  <th className="px-4 py-2 text-left">Response Rate</th>
                </tr>
              </thead>
              <tbody>
                {adherenceData.map((day, index) => (
                  <tr key={index} className="border-t">
                    <td className="px-4 py-2">{day.date}</td>
                    <td className="px-4 py-2">{day.remindersSent}</td>
                    <td className="px-4 py-2">{day.remindersResponded}</td>
                    <td className="px-4 py-2">
                      {((day.remindersResponded / day.remindersSent) * 100).toFixed(1)}%
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </div>

      <div className="bg-white rounded-lg shadow-lg p-6 border border-gray-200">
        <h3 className="text-xl font-semibold mb-4 text-gray-800 border-b pb-2">Treatment Plan Visualization</h3>
        <div className="mb-6">
          <h4 className="font-semibold mb-2">Treatment Timeline</h4>
          <div className="overflow-x-auto">
            <div className="min-w-max">
              {treatmentMilestones.map((milestone, index) => (
                <div key={index} className="flex items-center mb-4">
                  <div className="w-24 text-sm text-gray-600">{milestone.date}</div>
                  <div className="w-4 h-4 rounded-full bg-blue-500 mr-2"></div>
                  <div className="flex-grow">
                    <p className="font-medium">{milestone.description}</p>
                    <div className="flex items-center mt-1">
                      <input
                        type="range"
                        min="0"
                        max="100"
                        value={milestone.actualProgress}
                        onChange={(e) => updateMilestoneProgress(index, Number(e.target.value))}
                        className="w-full mr-2"
                      />
                      <span className="text-sm text-gray-600">{milestone.actualProgress}%</span>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
        <div>
          <h4 className="font-semibold mb-2">Progress Comparison</h4>
          <div className="h-64">
            <ResponsiveContainer width="100%" height="100%">
              <AreaChart data={treatmentMilestones}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="date" />
                <YAxis />
                <Tooltip />
                <Legend />
                <Area type="monotone" dataKey="expectedProgress" stroke="#8884d8" fill="#8884d8" name="Expected Progress" />
                <Area type="monotone" dataKey="actualProgress" stroke="#82ca9d" fill="#82ca9d" name="Actual Progress" />
              </AreaChart>
            </ResponsiveContainer>
          </div>
        </div>
      </div>

      <div className="bg-white rounded-lg shadow-lg p-6 border border-gray-200">
        <h3 className="text-xl font-semibold mb-4 text-gray-800 border-b pb-2">Treatment Efficacy Analysis</h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div>
            <h4 className="font-semibold mb-2">Treatment Efficacy Correlation</h4>
            <div className="h-64">
              <ResponsiveContainer width="100%" height="100%">
                <ScatterChart margin={{ top: 20, right: 20, bottom: 20, left: 20 }}>
                  <CartesianGrid />
                  <XAxis type="number" dataKey="frequency" name="Frequency" unit="sessions" />
                  <YAxis type="number" dataKey="painReduction" name="Pain Reduction" unit="points" />
                  <ZAxis type="category" dataKey="treatment" name="Treatment" />
                  <Tooltip cursor={{ strokeDasharray: '3 3' }} />
                  <Legend />
                  <Scatter name="Treatments" data={treatmentEfficacy} fill="#8884d8" />
                </ScatterChart>
              </ResponsiveContainer>
            </div>
          </div>
          <div>
            <h4 className="font-semibold mb-2">AI-Driven Recommendations</h4>
            <ul className="list-disc list-inside space-y-2">
              {aiRecommendations.map((recommendation, index) => (
                <li key={index} className="text-gray-700">{recommendation}</li>
              ))}
            </ul>
          </div>
        </div>
        <div className="mt-6">
          <h4 className="font-semibold mb-2">Treatment Efficacy Details</h4>
          <div className="overflow-x-auto">
            <table className="min-w-full bg-white">
              <thead className="bg-gray-100">
                <tr>
                  <th className="px-4 py-2 text-left">Treatment</th>
                  <th className="px-4 py-2 text-left">Pain Reduction</th>
                  <th className="px-4 py-2 text-left">Frequency</th>
                </tr>
              </thead>
              <tbody>
                {treatmentEfficacy.map((treatment, index) => (
                  <tr key={index} className="border-t">
                    <td className="px-4 py-2">{treatment.treatment}</td>
                    <td className="px-4 py-2">{treatment.painReduction.toFixed(1)} points</td>
                    <td className="px-4 py-2">{treatment.frequency} sessions</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PatientDetail;